import React, { useState, useRef, useEffect } from 'react';

/**
 * Floating "Help Genie" widget — bottom-right pill button that opens a
 * modal letting users send text feedback or record a short voice memo.
 *
 *   POST /api/v1/feedback/text   { text_body, reply_email?, platform, page_url, app_version, locale }
 *   POST /api/v1/feedback/voice  multipart/form-data audio + same metadata fields
 *
 * Voice recording uses the browser-native MediaRecorder API — no extra
 * dependency. Requires HTTPS in production (works on localhost).
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const MAX_SECONDS = 60;
const APP_VERSION = '1.0';   // bumps when the widget itself changes

export default function HelperGenie() {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('text');           // 'text' | 'voice'
    const [text, setText] = useState('');
    const [replyEmail, setReplyEmail] = useState('');
    const [status, setStatus] = useState('idle');       // idle | sending | success | error
    const [errorMsg, setErrorMsg] = useState('');

    // Voice state
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState('');
    const [audioDurationMs, setAudioDurationMs] = useState(0);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const recordStartRef = useRef(0);

    useEffect(() => () => stopTimer(), []);

    function stopTimer() {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }

    function resetVoice() {
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioBlob(null);
        setAudioUrl('');
        setAudioDurationMs(0);
        setElapsed(0);
        chunksRef.current = [];
    }

    function resetAll() {
        setText('');
        setReplyEmail('');
        setStatus('idle');
        setErrorMsg('');
        resetVoice();
    }

    async function startRecording() {
        setErrorMsg('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime = pickRecorderMime();
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
            rec.onstop = () => {
                stream.getTracks().forEach(t => t.stop());
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
                const url = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(url);
                setAudioDurationMs(Date.now() - recordStartRef.current);
                setRecording(false);
                stopTimer();
            };
            recorderRef.current = rec;
            recordStartRef.current = Date.now();
            rec.start();
            setRecording(true);
            setElapsed(0);
            timerRef.current = setInterval(() => {
                const sec = Math.floor((Date.now() - recordStartRef.current) / 1000);
                setElapsed(sec);
                if (sec >= MAX_SECONDS) stopRecording();
            }, 200);
        } catch (err) {
            console.warn('mic permission denied', err);
            setErrorMsg('Microphone access was blocked. Allow it in your browser or use Text instead.');
        }
    }

    function stopRecording() {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            try { recorderRef.current.stop(); } catch (_) {}
        }
    }

    function pickRecorderMime() {
        const candidates = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/mp4',
        ];
        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null;
        return candidates.find(c => MediaRecorder.isTypeSupported(c)) || null;
    }

    function metadataFields() {
        return {
            platform: 'web',
            app_version: APP_VERSION,
            locale: (navigator.language || '').slice(0, 16),
            page_url: window.location.pathname + window.location.search,
        };
    }

    async function submitText() {
        if (!text.trim()) {
            setErrorMsg('Please type your feedback first.');
            return;
        }
        setStatus('sending');
        setErrorMsg('');
        try {
            const res = await fetch(`${API_BASE}/v1/feedback/text`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text_body: text,
                    reply_email: replyEmail.trim() || undefined,
                    ...metadataFields(),
                }),
            });
            if (!res.ok) throw new Error(`http_${res.status}`);
            setStatus('success');
        } catch (err) {
            console.error(err);
            setStatus('error');
            setErrorMsg('Could not send your feedback. Please try again in a moment.');
        }
    }

    async function submitVoice() {
        if (!audioBlob) {
            setErrorMsg('Record a short clip first.');
            return;
        }
        setStatus('sending');
        setErrorMsg('');
        try {
            const fd = new FormData();
            const ext = audioBlob.type.includes('mp4') ? '.m4a'
                      : audioBlob.type.includes('ogg') ? '.ogg' : '.webm';
            fd.append('audio', audioBlob, `feedback${ext}`);
            if (text.trim()) fd.append('text_body', text.trim());
            if (replyEmail.trim()) fd.append('reply_email', replyEmail.trim());
            fd.append('audio_duration_ms', String(audioDurationMs));
            const meta = metadataFields();
            Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
            const res = await fetch(`${API_BASE}/v1/feedback/voice`, { method: 'POST', body: fd });
            if (!res.ok) throw new Error(`http_${res.status}`);
            setStatus('success');
        } catch (err) {
            console.error(err);
            setStatus('error');
            setErrorMsg('Could not upload your recording. Please try again in a moment.');
        }
    }

    function close() {
        if (status === 'sending') return;
        setOpen(false);
        if (status === 'success') {
            setTimeout(resetAll, 250);
        }
    }

    if (!open) {
        return (
            <button
                onClick={() => setOpen(true)}
                aria-label="Open feedback genie"
                className="fixed bottom-6 right-6 z-50 bg-zebcast-purple text-white rounded-full shadow-lg
                           px-5 py-3 flex items-center gap-2 hover:bg-zebcast-dark transition-colors"
                style={{ background: '#7c3aed' }}
            >
                <span aria-hidden style={{ fontSize: '1.2rem' }}>💬</span>
                <span className="text-sm font-semibold">Help &amp; Feedback</span>
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
             onClick={close}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
                 onClick={(e) => e.stopPropagation()}>
                {status === 'success' ? (
                    <div className="text-center py-6">
                        <div className="text-4xl mb-3">🎉</div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Thanks for the feedback!</h2>
                        <p className="text-gray-600 text-sm mb-4">
                            Our team reads every message. We&apos;ll reach out if you left an email.
                        </p>
                        <button onClick={close}
                                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm">
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-gray-900">Send feedback</h2>
                            <button onClick={close}
                                    className="text-gray-400 hover:text-gray-700"
                                    aria-label="Close">✕</button>
                        </div>

                        <div className="flex border-b mb-4">
                            <button
                                onClick={() => setMode('text')}
                                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    mode === 'text'
                                        ? 'border-zebcast-purple text-zebcast-purple'
                                        : 'border-transparent text-gray-500'
                                }`}
                                style={mode === 'text' ? { borderColor: '#7c3aed', color: '#7c3aed' } : {}}
                            >
                                ✎ Text
                            </button>
                            <button
                                onClick={() => setMode('voice')}
                                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    mode === 'voice'
                                        ? 'border-zebcast-purple text-zebcast-purple'
                                        : 'border-transparent text-gray-500'
                                }`}
                                style={mode === 'voice' ? { borderColor: '#7c3aed', color: '#7c3aed' } : {}}
                            >
                                🎤 Voice
                            </button>
                        </div>

                        {mode === 'text' ? (
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Tell us what's broken, what you love, or what you'd like to see…"
                                rows={5}
                                maxLength={4000}
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm
                                           focus:outline-none focus:border-zebcast-purple"
                            />
                        ) : (
                            <div className="border border-gray-300 rounded-lg p-4 text-center">
                                {!recording && !audioBlob && (
                                    <button onClick={startRecording}
                                            className="px-4 py-2 rounded-full bg-red-500 hover:bg-red-600
                                                       text-white text-sm font-semibold">
                                        ● Start recording
                                    </button>
                                )}
                                {recording && (
                                    <div>
                                        <div className="text-red-500 text-3xl mb-2 animate-pulse">●</div>
                                        <div className="text-gray-700 text-sm mb-3">
                                            Recording… {elapsed}s / {MAX_SECONDS}s
                                        </div>
                                        <button onClick={stopRecording}
                                                className="px-4 py-2 rounded-full bg-gray-700 hover:bg-gray-800
                                                           text-white text-sm">
                                            ■ Stop
                                        </button>
                                    </div>
                                )}
                                {!recording && audioBlob && (
                                    <div>
                                        <audio src={audioUrl} controls className="w-full mb-2" />
                                        <p className="text-xs text-gray-500 mb-2">
                                            {(audioDurationMs / 1000).toFixed(1)}s ·
                                            {' '}{(audioBlob.size / 1024).toFixed(0)} KB
                                        </p>
                                        <button onClick={resetVoice}
                                                className="text-xs text-gray-500 hover:text-gray-800 underline">
                                            Re-record
                                        </button>
                                        <textarea
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                            placeholder="Optional — add a written note"
                                            rows={2}
                                            maxLength={2000}
                                            className="mt-3 w-full border border-gray-300 rounded-lg p-2
                                                       text-sm focus:outline-none focus:border-zebcast-purple"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <input
                            type="email"
                            value={replyEmail}
                            onChange={(e) => setReplyEmail(e.target.value)}
                            placeholder="Your email (optional — only if you want a reply)"
                            className="mt-3 w-full border border-gray-300 rounded-lg p-2 text-sm
                                       focus:outline-none focus:border-zebcast-purple"
                        />

                        {errorMsg && (
                            <p className="mt-3 text-sm text-red-600">{errorMsg}</p>
                        )}

                        <div className="mt-4 flex gap-2">
                            <button onClick={close}
                                    disabled={status === 'sending'}
                                    className="flex-1 py-2 rounded-lg bg-gray-100 hover:bg-gray-200
                                               text-sm text-gray-700">
                                Cancel
                            </button>
                            <button
                                onClick={mode === 'text' ? submitText : submitVoice}
                                disabled={status === 'sending'}
                                className="flex-1 py-2 rounded-lg text-white text-sm font-semibold
                                           disabled:opacity-50"
                                style={{ background: '#7c3aed' }}
                            >
                                {status === 'sending' ? 'Sending…' : 'Send'}
                            </button>
                        </div>
                        <p className="mt-3 text-[11px] text-gray-400 leading-snug">
                            We use your message only to improve ZebCast. Voice clips are auto-transcribed
                            for review and may be deleted on request.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
